import axios, { AxiosResponseInterceptorParams } from 'axios';
import { isSuppressedStatusCode, logError } from '@utils/error-tracking';
import { appBasePath } from '@constants';
import { Toast } from 'types/layout';
import { useAppStore } from '@stores/app';

/**
 * This handler controls showing Error toasts and logging the error to Sentry/Datadog.
 * This interceptor handles the logic when `suppressToast` or a custom `errorToast` is passed to the axis config.
 * We suppress toasts/logging errors on the following status codes, as they all have their own unique handling: 401 (unauthorized), 540 (maintenance mode), 405 (WAF);
 * Additionally, we suppress any errors as a result of cornice substitute user read-only access and server validation response for forms. These errors are handled, and showing the toast/logging the error is unneeded.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getErrorToast(err: any): Toast {
	if (err.config?.errorToast) {
		return err.config.errorToast;
	}

	return {
		type: 'error',
		message:
			'An error has occurred. The error has been logged and we&rsquo;ll look into it immediately. If you need assistance, contact us at&nbsp;<a href="mailto:support@fundrise.com">support@fundrise.com</a>.'
	};
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function errorHandler(err: any): Promise<unknown> {
	const appStore = useAppStore();
	const statusMaintenanceMode = 540;
	const forbiddenAccessStatus = 403;
	const isSuppressedStatus = isSuppressedStatusCode(err);
	const isAborted = axios.isCancel(err);
	const isReadOnlyError = appStore.isReadOnlyAccess && (err.response?.status === forbiddenAccessStatus || isAborted);
	const isServerValidationResponse = !!err.response?.data?.fieldErrorMap;
	const isHandledError = isAborted || isServerValidationResponse;
	const suppressToast = err.config?.suppressToast?.(err.response) ?? false;

	if (statusMaintenanceMode === err.response?.status) {
		window.location.href = `${appBasePath}/maintenance`;
	}

	// If request fails due to a read-only access scope, swallow the error so page rendering isn't blocked.
	if (isReadOnlyError) {
		return Promise.resolve();
	}

	if (isSuppressedStatus || isHandledError || suppressToast) {
		return Promise.reject(err);
	}

	logError(err, 'error');
	appStore.addToast(getErrorToast(err));
	return Promise.reject(err);
}

export const errorHandlerInterceptor: AxiosResponseInterceptorParams = [
	(response) => response,
	(error) => errorHandler(error)
];
